import { Flag, MediaSet, ProductVariantRaw, StockStatus } from '#constants/types/algolia';
import {
  NorceFlagCodes,
  NorceFlagGroupCodes,
  NorceParametricCodes,
  NorceProductStatusCodes,
  NorceWarehouseCodes
} from '#constants/norceCodes';
import useStripFlagSuffix from '~/composables/useStripFlagSuffix';
const { stripFlagSuffix } = useStripFlagSuffix();

export type ProductVariantModel = ProductVariant & ProductVariantRaw
export default class ProductVariant {
  _ProductVariantRaw: ProductVariantRaw;
  constructor(ProductVariant: ProductVariantRaw) {
    this._ProductVariantRaw = ProductVariant;
    return new Proxy(this, {
      get(target, name, receiver) {
        if (Reflect.has(target, name)) {
          return Reflect.get(target, name, receiver);
        }
        return Reflect.get(target._ProductVariantRaw, name, receiver);
      },
    });
  }

  static create(arg: ProductVariantRaw) {
    return new ProductVariant(arg) as ProductVariantModel;
  }

  parametricByCode(code: string, onlyDefining = false) {
    if (onlyDefining) {
      return this._ProductVariantRaw.definingParametrics.find(p => p.code === code);
    }
    return this._ProductVariantRaw.parametrics.find(p => p.code === code);
  }

  get priceInfo() {
    return {
      price: this._ProductVariantRaw.priceInfo?.price || '',
      priceDisplay: this._ProductVariantRaw.priceInfo?.priceDisplay || '',
      priceBeforeDiscount: this._ProductVariantRaw.priceInfo?.priceBeforeDiscount || '',
      priceBeforeDiscountDisplay: this._ProductVariantRaw.priceInfo?.priceBeforeDiscountDisplay || '',
      priceBeforeVat: this._ProductVariantRaw.priceInfo?.priceBeforeVat || '',
      priceBeforeVatDisplay: this._ProductVariantRaw.priceInfo?.priceBeforeVatDisplay || '',
      priceBeforeDiscountBeforeVatDisplay: this._ProductVariantRaw.priceInfo?.priceBeforeDiscountBeforeVatDisplay || '',
      vatRate: this._ProductVariantRaw.priceInfo?.vatRate || 0,
      isOnSale: this._ProductVariantRaw.priceInfo.isOnSale ?? (this._ProductVariantRaw.priceInfo.price !== this._ProductVariantRaw.priceInfo.priceBeforeDiscount),
    };
  }

  get status() {
    return this._ProductVariantRaw.status ?? '';
  }

  /**
   * Get stock status by warehouse code
   * @param warehouseCode - The warehouse code to look up
   */
  getStockStatusByWarehouse(warehouseCode: string): StockStatus | null {
    return this._ProductVariantRaw.stockStatuses.find(st => st.warehouse.code === warehouseCode) ?? null;
  }

  /**
   * Get stock value by warehouse code
   * @param warehouseCode - The warehouse code to look up
   */
  getStockValueByWarehouse(warehouseCode: string): number {
    const stockStatus = this.getStockStatusByWarehouse(warehouseCode);
    return stockStatus?.inStock ?? 0;
  }

  /**
   * @deprecated Use getStockStatusByWarehouse(warehouseCode) from useWarehouse composable instead
   */
  get stockStatus(): StockStatus | null {
    return this._ProductVariantRaw.stockStatuses[0] ?? null;
  }

  get isActive(): boolean {
    return this.status === NorceProductStatusCodes.Active;
  }

  get isComing(): boolean {
    return this.status === NorceProductStatusCodes.Coming;
  }

  get isExpired(): boolean {
    return this.status === NorceProductStatusCodes.Expired;
  }

  get isExpiring(): boolean {
    return this.status === NorceProductStatusCodes.Expiring;
  }

  get isHidden(): boolean {
    return this.status === NorceProductStatusCodes.Hidden;
  }

  /**
   * @deprecated Use getStockStatusByWarehouse(warehouseCode) from useWarehouse composable instead
   */
  get standardStockStatus(): StockStatus | null {
    return this._ProductVariantRaw.stockStatuses[0] ?? null;
  }

  /**
   * @deprecated Use getStockValueByWarehouse(warehouseCode) from useWarehouse composable instead
   */
  get stockValue() {
    return this._ProductVariantRaw.stockStatuses[0]?.inStock ?? 0;
  }

  get stockValuePrimary() {
    return this._ProductVariantRaw.primaryStockStatus?.inStock ?? 0;
  }

  get hasIncomingDatePrimary() {
    const today = new Date().toISOString().split('T')[0];
    const primary = this._ProductVariantRaw.closestNextDelivery;
    return primary && primary > today;
  }

  get incomingDatePrimary() {
    return this._ProductVariantRaw.closestNextDelivery;
  }

  get url() {
    return this._ProductVariantRaw.url;
  }

  get hasIncomingDate() {
    const today = new Date().toISOString().split('T')[0];
    const stockStatus = this.standardStockStatus;
    return stockStatus?.estimatedDelivery && stockStatus.estimatedDelivery > today;
  }

  get incomingDate() {
    const today = new Date().toISOString().split('T')[0];
    const stockStatus = this.standardStockStatus;
    if (stockStatus?.estimatedDelivery && stockStatus.estimatedDelivery > today) {
      return stockStatus.estimatedDelivery.split('T')[0];
    }
    return null;
  }

  get isSoldOut() {
    return !this.stockValue && !this.incomingDate;
  }

  get isSoldOutPrimary() {
    return !this.stockValuePrimary && !this.incomingDatePrimary;
  }

  get isTempSoldOut() {
    return this.isActive && this.stockValue < 1 && this.hasIncomingDate;
  }

  get isTempSoldOutPrimary() {
    return this.isActive && this.stockValuePrimary < 1 && this.hasIncomingDatePrimary;
  }

  get flags() {
    return this._ProductVariantRaw.flags ?? [];
  }

  /**
   * Check if purchase is disabled via control flag
   */
  get isPurchaseDisabled(): boolean {
    return this.flags.some(f =>
      stripFlagSuffix(f.groupCode) === NorceFlagGroupCodes.ControlFlags &&
      stripFlagSuffix(f.code) === NorceFlagCodes.DisablePurchase
    );
  }

  get isPreOrderProduct() {
    return (this.isComing || this.isActive) && this.hasIncomingDate && this.flags.filter((flag: Flag) => stripFlagSuffix(flag.type) === NorceFlagCodes.PreOrder)?.length > 0;
  }

  get isOnDemandProduct() {
    return this.isActive && this.flags.filter((flag: Flag) => stripFlagSuffix(flag.type) === NorceFlagCodes.OnDemand)?.length > 0;
  }

  get productHasExpired() {
    return (this.isActive || this.isExpired || this.isHidden) && this.flags.filter((flag: Flag) => stripFlagSuffix(flag.type) === NorceFlagCodes.DiscontinuedAndVisible)?.length > 0;
  }

  get percentageCampaigns() {
    return this._ProductVariantRaw.flags.filter((f) => stripFlagSuffix(f.groupCode) === NorceFlagGroupCodes.PercentageCampaigns);
  }
  get stepCampaigns() {
    return this._ProductVariantRaw.flags.find((f) => stripFlagSuffix(f.groupCode) === NorceFlagGroupCodes.StepCampaigns);
  }
  get giftCampaigns() {
    return this._ProductVariantRaw.flags.find((f) => stripFlagSuffix(f.groupCode) === NorceFlagGroupCodes.GiftCampaigns);
  }

  get hasBuyMoreCampaign() {
    return this.stepCampaigns || this.percentageCampaigns.length > 0;
  }

  get hasFixedRecommendedQty() {
    return this._ProductVariantRaw.isRecommendedQtyFixed && (this._ProductVariantRaw.recommendedQty && this._ProductVariantRaw.recommendedQty > 0);
  }

  get relatedProducts() {
    return this._ProductVariantRaw.relatedProducts ?? [];
  }

  get isInStock() {
    return this.stockValue > 0;
  }

  /**
   * Check if variant can be added to cart (without backorders check)
   * For full check including backorders, use canAddToCart from component with globalContent.getAllowBackorders
   */
  get canAddToCart() {
    if (this.isPurchaseDisabled) {
      return false;
    }
    return this.isInStock || this.isPreOrderProduct || this.isOnDemandProduct || this.shouldCheckFutureAvailability;
  }

  get isOnSaleForUser() {
    return false; // MOCK
  }

  get volume() {
    return this.parametricByCode(NorceParametricCodes.Volume, true) || null;
  }

  get ingredients() {
    return this._ProductVariantRaw.parametrics?.find((p) => p.code === NorceParametricCodes.Ingredients)?.value ??
      this._ProductVariantRaw.productParametrics?.find((p) => p.code === NorceParametricCodes.Ingredients)?.value ?? null;
  }

  get variantDescription() {
    return this._ProductVariantRaw.parametrics?.find((p) => p.code === 'VariantDescription')?.value ?? '';
  }

  get shortVariantDescription() {
    return this._ProductVariantRaw.parametrics?.find((p) => p.code === 'ShortVariantDescription')?.value ?? '';
  }

  /**
   * If we should lookup future deliveries from api instead of norce
   */
  get shouldCheckFutureAvailability() {
    const shouldCheckFutureAvailability = this._ProductVariantRaw.productParametrics?.find(p => p.code === 'FutureAvailabilityApiCheck');
    return shouldCheckFutureAvailability?.value || false;
  }

  mediaSetByCode(code: string): MediaSet | undefined {
    return this._ProductVariantRaw.mediaSets?.find(f => f.code === code);
  }

  /**
   * @deprecated -  use related products on product level, not variant
   */
  relatedProductsByType(type: string) {
    console.log('Using deprecated method - always return empty. Type: ' + type);
    return [];
  }
}
